import { Header, Sidebar } from '@/components/layout';
import { ClinicalTemplateDialog } from '@/components/templates/ClinicalTemplateDialog';
import { ClinicalTemplateTable, IconTableButton } from '@/components/templates/ClinicalTemplateTable';
import { TemplateScopeTabs } from '@/components/templates/TemplateScopeTabs';
import { TodoAssignmentTable } from '@/components/templates/TodoAssignmentTable';
import {
  type ClinicalTemplate,
  type ClinicalTemplateFormState,
  type TodoAssignment,
  clinicalTemplatePayloadFromForm,
  emptyClinicalTemplateForm,
  fetchJson,
  formFromTemplate,
  visibilityLabelOptionsFromTemplates,
} from '@/components/templates/template-utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { hasSystemAdminAccess, useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

export default function TemplatesOrg() {
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ClinicalTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<ClinicalTemplateFormState>(emptyClinicalTemplateForm);
  const [isLoadingTemplateDetail, setIsLoadingTemplateDetail] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const activeOrgId = user?.authOrgId;
  const showTodoTemplateColumn = hasSystemAdminAccess(user);

  const { data: templates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['templates', 'org'],
    queryFn: () => fetchJson<ClinicalTemplate[]>('/api/templates/org'),
  });
  const { data: assignmentPayload, isLoading: isLoadingAssignments } = useQuery({
    queryKey: ['templates', 'todo-assignments', 'current-org', activeOrgId],
    queryFn: () =>
      activeOrgId
        ? fetchJson<{ assignments?: TodoAssignment[] }>(
            `/api/templates/todo/assignments?auth_org_id=${encodeURIComponent(activeOrgId)}`
          )
        : Promise.resolve({ assignments: [] }),
    enabled: Boolean(activeOrgId),
  });
  const assignments = assignmentPayload?.assignments || [];

  const saveTemplateMutation = useMutation({
    mutationFn: async ({ id, data }: { id?: number; data: ClinicalTemplateFormState }) => {
      const payload = clinicalTemplatePayloadFromForm(data, { includeVisibilityLabels: true });

      if (!id) {
        return fetchJson('/api/templates/org', {
          method: 'POST',
          body: JSON.stringify({ ...payload, auth_org_id: activeOrgId }),
        });
      }

      await fetchJson(`/api/templates/org/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return fetchJson(`/api/templates/org/${id}/visibility`, {
        method: 'PUT',
        body: JSON.stringify({ labels: payload.visibility_labels || [] }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'org'] });
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      setTemplateForm(emptyClinicalTemplateForm);
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að vista sniðmát.', variant: 'destructive' });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (id: number) => fetchJson(`/api/templates/org/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'org'] });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að eyða sniðmáti.', variant: 'destructive' });
    },
  });

  const toggleAssignmentMutation = useMutation({
    mutationFn: (assignment: TodoAssignment) =>
      fetchJson(`/api/templates/todo/assignments/${assignment.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !assignment.is_active }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', 'todo-assignments'] });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að breyta stöðu verkefnasniðs.', variant: 'destructive' });
    },
  });

  const openTemplateDialog = async (template?: ClinicalTemplate) => {
    if (!template) {
      setEditingTemplate(null);
      setTemplateForm(emptyClinicalTemplateForm);
      setTemplateDialogOpen(true);
      return;
    }

    setIsLoadingTemplateDetail(true);
    try {
      const fullTemplate = await fetchJson<ClinicalTemplate>(`/api/templates/${template.id}`);
      setEditingTemplate(fullTemplate);
      setTemplateForm(formFromTemplate(fullTemplate));
      setTemplateDialogOpen(true);
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að sækja sniðmát.', variant: 'destructive' });
    } finally {
      setIsLoadingTemplateDetail(false);
    }
  };

  const saveTemplate = (formValues: ClinicalTemplateFormState) => {
    saveTemplateMutation.mutate({ id: editingTemplate?.id, data: formValues });
  };

  const deleteTemplate = (template: ClinicalTemplate) => {
    if (!window.confirm(`Eyða ${template.name}?`)) return;
    deleteTemplateMutation.mutate(template.id);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Sniðmát stofnunar" />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-6xl space-y-6">
            <TemplateScopeTabs active="org" />

            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="font-bold text-2xl text-slate-900">Sniðmát stofnunar</h1>
                <p className="text-muted-foreground text-sm">Stýring á klínískum sniðmátum fyrir núverandi stofnun.</p>
              </div>
              <Button onClick={() => openTemplateDialog()} className="gap-2">
                <Plus className="h-4 w-4" />
                Nýtt sniðmát
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5" />
                  Klínísk sniðmát
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingTemplates ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <ClinicalTemplateTable
                    templates={templates}
                    emptyText="Engin stofnunarsniðmát fundust."
                    renderActions={(template) => (
                      <>
                        <IconTableButton
                          label={`Breyta ${template.name}`}
                          onClick={() => openTemplateDialog(template)}
                          disabled={isLoadingTemplateDetail}
                        >
                          <Pencil className="h-4 w-4" />
                        </IconTableButton>
                        <IconTableButton
                          label={`Eyða ${template.name}`}
                          onClick={() => deleteTemplate(template)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </IconTableButton>
                      </>
                    )}
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="font-semibold text-lg leading-none tracking-tight">Verkefnasnið</h2>
              </CardHeader>
              <CardContent>
                {isLoadingAssignments ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <TodoAssignmentTable
                    assignments={assignments}
                    onToggle={toggleAssignmentMutation.mutate}
                    showTemplateColumn={showTodoTemplateColumn}
                    isToggling={toggleAssignmentMutation.isPending}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      <ClinicalTemplateDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        title={editingTemplate ? 'Breyta stofnunarsniðmáti' : 'Nýtt stofnunarsniðmát'}
        description="Vistaðu efni sniðmáts og valfrjálst merki/hlutverk."
        form={templateForm}
        onSubmit={saveTemplate}
        isSaving={saveTemplateMutation.isPending}
        showVisibilityLabels
        visibilityLabelOptions={visibilityLabelOptionsFromTemplates(templates, templateForm.visibility_labels)}
      />
    </div>
  );
}
